<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  contactId: {
    type: [String, Number],
    required: true,
  },
});

const store = useStore();
const router = useRouter();
const { t } = useI18n();

const orders = ref([]);
const isLoading = ref(false);

const hasOrders = computed(() => orders.value.length > 0);

const fetchOrders = async () => {
  if (!props.contactId) return;

  isLoading.value = true;
  try {
    const response = await store.dispatch(
      'orders/getByContact',
      props.contactId
    );
    orders.value = response || [];
  } catch (error) {
    orders.value = [];
  } finally {
    isLoading.value = false;
  }
};

const statusColor = status => {
  const mapping = {
    pending: 'gray',
    processing: 'blue',
    shipped: 'orange',
    delivered: 'green',
    cancelled: 'red',
    refunded: 'red',
  };
  return mapping[status] || 'gray';
};

const formatPrice = (cents, currency = 'USD') => {
  if (!cents || Number.isNaN(cents)) return '-';
  return `${(cents / 100).toFixed(2)} ${currency}`;
};

const formatDate = ts => {
  if (!ts || ts === 0) return '-';
  const date = new Date(ts * 1000);
  return date.toLocaleDateString('vi-VN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const openOrderDetails = order => {
  const accountId = router.currentRoute.value.params.accountId;
  const url = `/app/accounts/${accountId}/orders/${order.id}`;
  window.open(url, '_blank');
};

const getTrackingStatusColor = status => {
  const mapping = {
    Delivered:
      'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-200',
    'In Transit':
      'bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-200',
    'Out for Delivery':
      'bg-orange-100 text-orange-800 dark:bg-orange-800 dark:text-orange-200',
    Pending:
      'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
    Shipped:
      'bg-purple-100 text-purple-800 dark:bg-purple-800 dark:text-purple-200',
  };
  return (
    mapping[status] ||
    'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
  );
};

const getTrackingStatusText = status => {
  const mapping = {
    Delivered: t('SHIPMENTS.DELIVERED'),
    'In Transit': t('SHIPMENTS.IN_TRANSIT'),
    'Out for Delivery': t('SHIPMENTS.OUT_FOR_DELIVERY'),
    Pending: t('SHIPMENTS.PENDING'),
    Shipped: t('SHIPMENTS.SHIPPED'),
  };
  return mapping[status] || status;
};

watch(() => props.contactId, fetchOrders, { immediate: true });

onMounted(fetchOrders);
</script>

<template>
  <div class="contact-orders--panel">
    <div v-if="isLoading" class="flex items-center justify-center py-4">
      <Spinner />
    </div>
    <div v-else-if="!hasOrders" class="no-orders-message px-4 py-3">
      <span class="text-slate-500 dark:text-slate-400">
        {{ t('CONTACT_ORDERS.NO_RECORDS_FOUND') }}
      </span>
    </div>
    <div v-else class="contact-orders--list space-y-3 p-3">
      <div
        v-for="order in orders"
        :key="order.id"
        class="order-item bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-3 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-750 transition-colors"
        @click="openOrderDetails(order)"
      >
        <div class="flex justify-between items-start mb-2">
          <div class="flex items-center gap-2">
            <fluent-icon
              icon="receipt"
              size="14"
              class="text-slate-500 dark:text-slate-400"
            />
            <span
              class="text-sm font-medium text-slate-900 dark:text-slate-100"
            >
              #{{ order.external_order_id }}
            </span>
          </div>
          <woot-label
            :title="order.status"
            :color="statusColor(order.status)"
            variant="smooth"
            small
          />
        </div>
        <div
          class="flex justify-between items-center text-xs text-slate-600 dark:text-slate-400"
        >
          <span>{{ formatDate(order.placed_at) }}</span>
          <span class="font-medium text-slate-900 dark:text-slate-100">
            {{ formatPrice(order.total_cents, order.currency_code) }}
          </span>
        </div>

        <!-- Shipments Info -->
        <div
          v-if="order.shipments_summary"
          class="mt-2 pt-2 border-t border-slate-200 dark:border-slate-700"
        >
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-2">
              <fluent-icon
                icon="shipping-truck"
                size="12"
                class="text-slate-500 dark:text-slate-400"
              />
              <span class="text-xs text-slate-600 dark:text-slate-400">
                {{
                  order.shipments_summary.main_shipment.carrier_name ||
                  t('CARRIER')
                }}
              </span>
              <span
                v-if="order.shipments_summary.count > 1"
                class="text-xs text-slate-500 dark:text-slate-400"
              >
                ({{ order.shipments_summary.count }}
                {{ t('SHIPMENTS.SHIPMENTS') }})
              </span>
            </div>
            <div class="flex items-center gap-2">
              <span
                v-if="order.shipments_summary.main_shipment.tracking_code"
                class="text-xs font-mono text-slate-600 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded"
              >
                {{ order.shipments_summary.main_shipment.tracking_code }}
              </span>
              <a
                v-if="order.shipments_summary.main_shipment.tracking_url"
                :href="order.shipments_summary.main_shipment.tracking_url"
                target="_blank"
                rel="noopener noreferrer"
                class="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                @click.stop
              >
                <fluent-icon icon="open" size="12" />
              </a>
            </div>
          </div>
          <div
            v-if="order.shipments_summary.main_shipment.major_tracking_state"
            class="mt-1"
          >
            <span
              class="inline-flex px-2 py-1 text-xs font-medium rounded-full"
              :class="
                getTrackingStatusColor(
                  order.shipments_summary.main_shipment.major_tracking_state
                )
              "
            >
              {{
                getTrackingStatusText(
                  order.shipments_summary.main_shipment.major_tracking_state
                )
              }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.contact-orders--panel {
  .order-item {
    border: 1px solid var(--color-border-light);

    &:hover {
      border-color: var(--color-border);
    }
  }
}
</style>